import React, { useEffect, useMemo, useState } from 'react';
import classNames from 'classnames';
import { useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { activeChatbotSelector } from '@mm/state/selectors/viewSelectors';
import { useClickOutside } from '@mm/hooks';
import { activeFacebookPageSelector } from '@mm/state/selectors/facebookPageSelector';
import { Circle } from '@mm/components';
import { CloseOutlined, LeftOutlined, RightOutlined } from '@ant-design/icons';
import { UserRoleContainer } from '../../../../common/hooks/use-route-guard';
import { BotBuilderRoutes } from '../../../../routes/bot-builder-routes';
import applicationsIconSrc from './icons/ellipsis.svg';
import amonestationSrc from './icons/amonestation.svg';
import channelsSrc from './icons/channels.svg';
import chatblasterSrc from './icons/chatblaster.svg';
import flowchartSrc from './icons/flowchart.svg';
import librarySrc from './icons/library.svg';
import lightingBoltSrc from './icons/lightning-bolt.svg';
import megaphoneSrc from './icons/megaphone.svg';
import qaSrc from './icons/qa.svg';
import inboxSrc from './icons/inbox.svg';
import settingsSrc from './icons/settings.svg';
import helpDocsSrc from 'assets/icons/helpdocs.svg';
import * as routes from '../../../../common/constants/routes';
import { HELP_BETA } from '../../../../common/constants/externalRoutes';
import { useWhatsNewItems } from '../../../../editor/components/SlideoutMenu/menus/SlideoutWhatsNew/useWhatsNewItems';
import { colors } from '../../../../editor/features/beta/styles/colors';
import css from './AllFeaturesDropdown.module.css';

type Props = {
  className?: string;
  onItemClick?: (id: NavId) => void;
  isMobile?: boolean;
};

const MENU_ITEMS = {
  // Temporary disabled per request
  // messages: { title: 'Messages', icon: emailSrc },
  // templates: { title: 'Chatbot Templates', icon: layoutSrc },
  inbox: { title: 'Inbox', icon: inboxSrc },
  starters: { title: 'Chat-starters', icon: lightingBoltSrc },
  dialogues: { title: 'Dialogue Library', icon: librarySrc },
  qa: { title: 'Q&A', icon: qaSrc },
  blasters: { title: 'Chat Blaster', icon: chatblasterSrc },
  drips: { title: 'Drip Campaigns', icon: flowchartSrc },
  messenger_ads: { title: 'Facebook Messenger Ads', icon: channelsSrc },
  sponsored: { title: 'Sponsored Messages', icon: megaphoneSrc },
  rules: { title: 'Automation Rules', icon: amonestationSrc },
  settings: { title: 'Settings', icon: settingsSrc },
  help: { title: 'Help Docs', icon: helpDocsSrc },
};

type NavId = keyof typeof MENU_ITEMS;

export const useAllFeaturesList = () => {
  const history = useHistory();
  // const dispatch: Dispatch<StoreState> = useDispatch();

  const chatbot = useSelector(activeChatbotSelector);
  const chatbotId = chatbot.id;

  const {
    roleFlags: { isLiveChatAgent, isMonitorLeadsRole, isLimitedRole },
  } = UserRoleContainer.useContainer();

  const activeFacebookPage = useSelector(activeFacebookPageSelector);
  const onlyMaintenance =
    activeFacebookPage &&
    !activeFacebookPage.subscription &&
    !activeFacebookPage.redemption;

  const onSettingsClick = () => {
    history.push(
      onlyMaintenance
        ? routes.SETTINGS_MAINTENANCE(chatbot.id)
        : routes.CHATBOT_EDITOR_SETTINGS(chatbot.id),
    );
  };

  const onClick = (id: NavId) => {
    if (!chatbotId) {
      return;
    }
    switch (id) {
      case 'inbox':
        return history.push(
          BotBuilderRoutes.liveChat.getLink({
            chatbotId,
          }),
        );
      case 'blasters':
        return history.push(
          BotBuilderRoutes.campaigns.getLink({
            chatbotId,
            kind: 'chat_blaster',
          }),
        );
      case 'drips':
        return history.push(
          BotBuilderRoutes.campaigns.getLink({
            chatbotId,
            kind: 'drip_campaigns',
          }),
        );
      case 'sponsored':
        return history.push(
          BotBuilderRoutes.campaigns.getLink({
            chatbotId,
            kind: 'sponsored_messages',
          }),
        );
      case 'messenger_ads':
        return history.push(
          BotBuilderRoutes.campaigns.getLink({
            chatbotId,
            kind: 'ctm_ads',
          }),
        );
      case 'dialogues':
        return history.push(BotBuilderRoutes.build.getLink({ chatbotId }));
      case 'qa':
        return history.push(BotBuilderRoutes.qa.getLink({ chatbotId }));
      case 'settings':
        return onSettingsClick();
      case 'starters':
        return history.push(
          BotBuilderRoutes.chatStarters.getLink({ chatbotId }),
        );
      case 'rules':
        return history.push(
          BotBuilderRoutes.automationRules.getLink({
            chatbotId,
            kind: 'system_messages',
          }),
        );
      case 'help':
        return window.open(HELP_BETA);
      // case 'templates':
      //   return '';
      // case 'messages':
      //   return dispatch(showSlideoutMenu('whatsNew'));
    }
  };

  const items = useMemo(() => {
    return Object.entries(MENU_ITEMS).filter(([id]) => {
      return (
        (['settings', 'messages', 'help'].includes(id) && isLiveChatAgent) ||
        (['help'].includes(id) && isMonitorLeadsRole) ||
        !isLimitedRole
      );
    });
  }, [chatbotId, isLiveChatAgent, isMonitorLeadsRole, MENU_ITEMS]);

  return {
    items,
    onClick,
  };
};

export const AllFeaturesDropdown = ({
  className,
  onItemClick,
  isMobile,
}: Props) => {
  const chatbot = useSelector(activeChatbotSelector);

  const [isOpen, setIsOpen] = useState(false);

  const allFeatures = useAllFeaturesList();

  const onClick = () => {
    if (!isOpen) {
      setIsOpen(true);
    }
  };

  const onOutsideClick = () => {
    if (isOpen) {
      setIsOpen(false);
    }
  };

  // const { retrieveAll } = useWhatsNewCRUDActions();

  useEffect(() => {
    // temporary disabled per request
    // retrieveAll();
  }, []);

  const { filteredItems } = useWhatsNewItems();

  const ref = useClickOutside<HTMLDivElement>(onOutsideClick);

  if (!chatbot) {
    return <></>;
  }

  return (
    <div
      className={classNames(className, css.container, {
        [css.open]: isOpen,
      })}
      onClick={onClick}
    >
      <div className={css.button}>
        <span className={css.icon}>
          <img src={applicationsIconSrc} alt="Applications" />
        </span>
        {isMobile && (
          <>
            <span className={css.text}>Old features</span>
            <RightOutlined className="mobile-header__right-arrow" />
          </>
        )}
      </div>

      {isOpen && (
        <div className={css.dropdown} ref={ref}>
          {isMobile && (
            <div className={css.backMenuWrapper} onClick={onOutsideClick}>
              <LeftOutlined className={css.backArrow} />
              Main menu
            </div>
          )}

          {!isMobile && (
            <span className={css.close} onClick={onOutsideClick}>
              <CloseOutlined />
            </span>
          )}

          <span className={css.heading}>Old features</span>

          <div className={css.grid}>
            {allFeatures.items.map(([id, item]) => (
              <div
                className={css.item}
                key={id}
                onClick={async () => {
                  await allFeatures.onClick(id as NavId);
                  onItemClick?.(id as NavId);
                  setIsOpen(false);
                }}
              >
                <span className={css.icon}>
                  {id === 'messages' && !!filteredItems.length && (
                    <Circle color={colors.red} className={css.badge} />
                  )}
                  <img src={item.icon} alt={id} />
                </span>
                <span className={css.title}>{item.title}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
